import { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../lib/api';
import { ProjectSelect } from '../components/ProjectSelect';
import { cn, hoursToDays } from '../lib/utils';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay, differenceInCalendarDays, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    Calendar,
    Clock,
    Plus,
    Save,
    Send,
    Trash2,
    Loader2,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    AlertCircle,
} from 'lucide-react';
import type { ApiResponse, TimeEntry, Project } from '../types';

export function TimeEntry() {
    const { date: dateParam } = useParams();
    const navigate = useNavigate();
    const api = useApi();
    const queryClient = useQueryClient();

    const [selectedDate, setSelectedDate] = useState(
        dateParam ? new Date(dateParam) : new Date()
    );
    const [showForm, setShowForm] = useState(false);
    const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);

    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    // Fetch entries for selected date
    const { data: entriesData, isLoading } = useQuery({
        queryKey: ['time-entries', dateStr],
        queryFn: () =>
            api.get<ApiResponse<TimeEntry[]>>('/api/time-entries', {
                startDate: dateStr,
                endDate: dateStr,
                myEntries: true,
            }),
    });

    // Fetch projects
    const { data: projectsData } = useQuery({
        queryKey: ['projects', 'active'],
        queryFn: () =>
            api.get<ApiResponse<Project[]>>('/api/projects', {
                active: true,
                myProjects: true,
            }),
    });

    const entries = entriesData?.data || [];
    const projects = projectsData?.data || [];

    const totalDays = hoursToDays(entries.reduce((sum, e) => sum + e.hours, 0));

    const openNewEntry = () => {
        setEditingEntry(null);
        setShowForm(true);
    };

    const openEditEntry = (entry: TimeEntry) => {
        setEditingEntry(entry);
        setShowForm(true);
    };

    const dateInputRef = useRef<HTMLInputElement>(null);

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            const newDate = new Date(e.target.value + 'T12:00:00'); // Éviter les problèmes de timezone
            setSelectedDate(newDate);
            navigate(`/time-entry/${e.target.value}`);
        }
    };

    const openDatePicker = () => {
        dateInputRef.current?.showPicker();
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header with date picker */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <input
                            ref={dateInputRef}
                            type="date"
                            value={format(selectedDate, 'yyyy-MM-dd')}
                            onChange={handleDateChange}
                            className="sr-only"
                        />
                        <button
                            type="button"
                            onClick={openDatePicker}
                            className="flex items-center gap-3 px-4 py-2 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg cursor-pointer hover:border-[var(--color-primary)] transition-colors"
                        >
                            <Calendar className="w-5 h-5 text-[var(--color-primary)]" />
                            <div className="text-left">
                                <p className="font-semibold text-[var(--color-text)]">
                                    {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
                                </p>
                                <p className="text-xs text-[var(--color-text-secondary)]">
                                    {format(selectedDate, 'yyyy')}
                                </p>
                            </div>
                        </button>
                    </div>
                </div>

                <button
                    onClick={openNewEntry}
                    className="btn-primary w-full sm:w-auto justify-center"
                >
                    <Plus className="w-4 h-4" />
                    Ajouter
                </button>
            </div>

            {/* Total hours */}
            <div className="card">
                <div className="flex items-center justify-between">
                    <span className="text-[var(--color-text-secondary)]">Total du jour</span>
                    <span
                        className={cn(
                            'text-2xl font-bold',
                            totalDays >= 1 ? 'text-[var(--color-success)]' : 'text-[var(--color-text)]'
                        )}
                    >
                        {totalDays}j
                    </span>
                </div>
                {totalDays < 1 && totalDays > 0 && (
                    <p className="text-sm text-[var(--color-warning)] mt-2">
                        Il manque {Math.round((1 - totalDays) * 100) / 100}j pour atteindre la journée standard
                    </p>
                )}
            </div>

            {/* Entries list */}
            <div className="space-y-3">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                    </div>
                ) : entries.length === 0 ? (
                    <div className="text-center py-12 card">
                        <p className="text-[var(--color-text-secondary)]">Aucune saisie pour cette journée</p>
                        <button
                            onClick={openNewEntry}
                            className="mt-4 text-[var(--color-primary)] hover:underline"
                        >
                            Ajouter une entrée
                        </button>
                    </div>
                ) : (
                    entries.map((entry) => (
                        <EntryCard
                            key={entry.id}
                            entry={entry}
                            onEdit={() => openEditEntry(entry)}
                        />
                    ))
                )}
            </div>

            {/* Entry Form Modal */}
            {showForm && (
                <EntryFormModal
                    entry={editingEntry}
                    date={dateStr}
                    selectedDate={selectedDate}
                    projects={projects}
                    onClose={() => setShowForm(false)}
                    onSaved={() => {
                        setShowForm(false);
                        queryClient.invalidateQueries({ queryKey: ['time-entries'] });
                    }}
                />
            )}
        </div>
    );
}

interface EntryCardProps {
    entry: TimeEntry;
    onEdit: () => void;
}

function EntryCard({ entry, onEdit }: EntryCardProps) {
    const api = useApi();
    const queryClient = useQueryClient();

    const submitMutation = useMutation({
        mutationFn: () => api.post(`/api/time-entries/${entry.id}/submit`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => api.delete(`/api/time-entries/${entry.id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        },
    });

    const statusColors = {
        draft: 'badge-draft',
        submitted: 'badge-submitted',
        approved: 'badge-approved',
        rejected: 'badge-rejected',
        billed: 'badge-billed',
    };

    const statusLabels = {
        draft: 'Brouillon',
        submitted: 'Soumis',
        approved: 'Validé',
        rejected: 'Rejeté',
        billed: 'Facturé',
    };

    return (
        <div className="card">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-medium text-[var(--color-text)]">
                            {entry.projectCode}
                        </span>
                        <span className={statusColors[entry.status]}>
                            {statusLabels[entry.status]}
                        </span>
                    </div>
                    <p className="text-sm text-[var(--color-text-secondary)]">{entry.projectName}</p>
                    {entry.description && (
                        <p className="text-sm text-[var(--color-text-muted)] mt-2">{entry.description}</p>
                    )}
                    {entry.rejectionReason && (
                        <p className="text-sm text-[var(--color-error)] mt-2">
                            Motif du rejet : {entry.rejectionReason}
                        </p>
                    )}
                </div>

                <div className="text-right flex-shrink-0">
                    <span className="text-xl font-bold text-[var(--color-text)]">{hoursToDays(entry.hours)}j</span>
                    {entry.taskType && (
                        <p className="text-xs text-[var(--color-text-muted)] mt-1">{entry.taskType}</p>
                    )}
                </div>
            </div>

            {/* Actions - Allow modification for draft, submitted (not yet approved), and rejected */}
            {(entry.status === 'draft' || entry.status === 'submitted' || entry.status === 'rejected') && (
                <div className="flex flex-wrap items-center justify-end gap-2 mt-4 pt-4 border-t border-[var(--color-border)]">
                    <button
                        onClick={() => deleteMutation.mutate()}
                        className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-error-light)] rounded-lg transition-colors"
                        disabled={deleteMutation.isPending}
                        aria-label="Supprimer la saisie"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                    {/* Projet clôturé : l'API refuse toute modification, la saisie reste
                        supprimable et suit son cours de validation. */}
                    {entry.projectClosed ? (
                        <span className="text-xs text-[var(--color-text-muted)]">Projet clôturé</span>
                    ) : (
                        <button
                            onClick={onEdit}
                            className="btn-secondary"
                        >
                            Modifier
                        </button>
                    )}
                    {entry.status !== 'submitted' && (
                        <button
                            onClick={() => submitMutation.mutate()}
                            className="btn-primary"
                            disabled={submitMutation.isPending}
                        >
                            <Send className="w-3 h-3" />
                            Soumettre
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

interface EntryFormModalProps {
    entry: TimeEntry | null;
    date: string;
    selectedDate: Date;
    projects: Project[];
    onClose: () => void;
    onSaved: () => void;
}

function EntryFormModal({ entry, date, selectedDate, projects, onClose, onSaved }: EntryFormModalProps) {
    const api = useApi();
    const [formData, setFormData] = useState({
        projectId: entry?.projectId || '',
        // Convert stored hours to days for display (hours / 8)
        days: entry?.hours ? (entry.hours / 8).toString() : '',
        description: entry?.description || '',
        taskType: entry?.taskType || '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Multi-day selection state (only for new entries)
    const [isMultiDay, setIsMultiDay] = useState(false);
    const [multiDayMode, setMultiDayMode] = useState<'week' | 'month'>('week');
    const [selectedDays, setSelectedDays] = useState<string[]>([date]);
    const [calendarMonth, setCalendarMonth] = useState(selectedDate);

    // Get week days for the current selected date
    const weekDays = useMemo(() => {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 }); // Sunday
        return eachDayOfInterval({ start: weekStart, end: weekEnd });
    }, [selectedDate]);

    // Get month calendar grid for the calendar month
    const monthCalendar = useMemo(() => {
        const mStart = startOfMonth(calendarMonth);
        const mEnd = endOfMonth(calendarMonth);
        const daysInMonth = eachDayOfInterval({ start: mStart, end: mEnd });
        // getDay: 0=Sunday, we want Monday=0, so shift
        const firstDayOfWeek = (getDay(mStart) + 6) % 7; // 0=Monday
        return { daysInMonth, firstDayOfWeek, mStart };
    }, [calendarMonth]);

    const selectAllWorkdays = () => {
        const workdays = monthCalendar.daysInMonth
            .filter(d => d.getDay() !== 0 && d.getDay() !== 6)
            .map(d => format(d, 'yyyy-MM-dd'));
        setSelectedDays(workdays);
    };

    const clearMonthSelection = () => {
        setSelectedDays([]);
    };

    const toggleDay = (dayStr: string) => {
        setSelectedDays(prev =>
            prev.includes(dayStr)
                ? prev.filter(d => d !== dayStr)
                : [...prev, dayStr]
        );
    };

    // Reset selected days when toggling multi-day mode
    const handleMultiDayToggle = () => {
        if (!isMultiDay) {
            setSelectedDays([date]); // Start with current day selected
            setMultiDayMode('week');
        } else {
            setSelectedDays([date]); // Reset to single day
            setMultiDayMode('week');
        }
        setIsMultiDay(!isMultiDay);
    };

    const handleMultiDayModeChange = (mode: 'week' | 'month') => {
        setMultiDayMode(mode);
        setSelectedDays([date]); // Reset selection when switching mode
        if (mode === 'month') {
            setCalendarMonth(selectedDate);
        }
    };

    const handleSubmit = async (e: React.FormEvent, submitAfterSave: boolean = false) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        // Le champ de recherche n'est pas un `<select required>` : c'est ici que le projet
        // manquant est refusé, sinon le formulaire partirait sans projet.
        if (!formData.projectId) {
            setError('Veuillez choisir un projet');
            setIsSubmitting(false);
            return;
        }

        // Convert days to hours for storage (days * 8)
        const hoursToStore = parseFloat(formData.days) * 8;

        try {
            let createdEntryIds: string[] = [];

            if (entry) {
                // Edit mode - single entry
                await api.put(`/api/time-entries/${entry.id}`, {
                    projectId: formData.projectId,
                    description: formData.description,
                    taskType: formData.taskType,
                    hours: hoursToStore,
                });
                createdEntryIds = [entry.id];
            } else if (isMultiDay && selectedDays.length > 1) {
                // Multi-day creation
                const response = await api.post<{ data: { id: string }[] }>('/api/time-entries/batch', {
                    projectId: formData.projectId,
                    description: formData.description,
                    taskType: formData.taskType,
                    hours: hoursToStore,
                    dates: selectedDays,
                });
                createdEntryIds = response.data?.map((e: { id: string }) => e.id) || [];
            } else {
                // Single day creation
                const response = await api.post<{ data: { id: string } }>('/api/time-entries', {
                    projectId: formData.projectId,
                    description: formData.description,
                    taskType: formData.taskType,
                    entryDate: selectedDays[0] || date,
                    hours: hoursToStore,
                });
                createdEntryIds = [response.data?.id];
            }

            // If submitAfterSave is true, submit the entries for approval
            if (submitAfterSave && createdEntryIds.length > 0) {
                for (const entryId of createdEntryIds) {
                    if (entryId) {
                        await api.post(`/api/time-entries/${entryId}/submit`);
                    }
                }
            }

            onSaved();
        } catch (err) {
            setError((err as Error).message || 'Une erreur est survenue');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="-m-6 mb-4 p-6 border-b border-[var(--color-border)]">
                    <h2 className="text-lg font-semibold text-[var(--color-text)]">
                        {entry ? 'Modifier la saisie' : 'Nouvelle saisie'}
                    </h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="p-3 bg-[var(--color-error-light)] border border-[var(--color-error)] rounded-lg text-[var(--color-error)] text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Projet *
                        </label>
                        {/* Le projet d'une saisie déjà enregistrée reste proposé même s'il
                            n'est plus dans la liste, un projet clôturé en sortant : sans cela
                            le champ paraîtrait vide et la ligne changerait de projet au
                            premier enregistrement. */}
                        <ProjectSelect
                            projects={projects}
                            value={formData.projectId}
                            onChange={(projectId) => setFormData({ ...formData, projectId })}
                            extra={
                                entry?.projectId
                                    ? {
                                        id: entry.projectId,
                                        label: `${entry.projectCode ? entry.projectCode + ' - ' : ''}${entry.projectName || 'Projet clôturé'}`,
                                        note: 'saisie fermée',
                                    }
                                    : null
                            }
                            autoFocus={!entry}
                        />
                    </div>

                    {/* Alerte sur la date de fin du projet sélectionné */}
                    <ProjectEndDateWarning project={projects.find(p => p.id === formData.projectId)} />

                    {/* Multi-day toggle (only for new entries) */}
                    {!entry && (
                        <div className="flex items-center justify-between p-3 bg-[var(--color-bg)] rounded-lg">
                            <div className="flex items-center gap-2">
                                <CalendarDays className="w-4 h-4 text-[var(--color-primary)]" />
                                <span className="text-sm font-medium text-[var(--color-text-secondary)]">
                                    Saisie multi-jours
                                </span>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={isMultiDay}
                                onClick={handleMultiDayToggle}
                                className={cn(
                                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                                    isMultiDay ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-bg-hover)]'
                                )}
                            >
                                <span
                                    className={cn(
                                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition',
                                        isMultiDay ? 'translate-x-5' : 'translate-x-0'
                                    )}
                                />
                            </button>
                        </div>
                    )}

                    {/* Day selection grid (only shown in multi-day mode for new entries) */}
                    {!entry && isMultiDay && (
                        <div className="space-y-2">
                            {/* Mode selector tabs */}
                            <div className="flex rounded-lg bg-[var(--color-bg)] p-1 gap-1">
                                <button
                                    type="button"
                                    onClick={() => handleMultiDayModeChange('week')}
                                    className={cn(
                                        'flex-1 text-xs font-medium py-1.5 px-3 rounded-md transition-all',
                                        multiDayMode === 'week'
                                            ? 'bg-[var(--color-bg-card)] text-[var(--color-primary)] shadow-sm'
                                            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                                    )}
                                >
                                    Semaine
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleMultiDayModeChange('month')}
                                    className={cn(
                                        'flex-1 text-xs font-medium py-1.5 px-3 rounded-md transition-all',
                                        multiDayMode === 'month'
                                            ? 'bg-[var(--color-bg-card)] text-[var(--color-primary)] shadow-sm'
                                            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                                    )}
                                >
                                    Mois
                                </button>
                            </div>

                            <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                                Jours à saisir *
                            </label>

                            {/* Week mode */}
                            {multiDayMode === 'week' && (
                                <div className="grid grid-cols-7 gap-1">
                                    {weekDays.map((day) => {
                                        const dayStr = format(day, 'yyyy-MM-dd');
                                        const isSelected = selectedDays.includes(dayStr);
                                        const isToday = isSameDay(day, new Date());
                                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                                        return (
                                            <button
                                                key={dayStr}
                                                type="button"
                                                onClick={() => toggleDay(dayStr)}
                                                className={cn(
                                                    'flex flex-col items-center p-2 rounded-lg border-2 transition-all text-xs',
                                                    isSelected
                                                        ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                                                        : 'border-transparent bg-[var(--color-bg)] hover:bg-[var(--color-bg-hover)]',
                                                    isWeekend && !isSelected && 'opacity-50'
                                                )}
                                            >
                                                <span className="font-medium uppercase">
                                                    {format(day, 'EEE', { locale: fr })}
                                                </span>
                                                <span className={cn(
                                                    'text-lg font-bold',
                                                    isToday && 'text-[var(--color-primary)]'
                                                )}>
                                                    {format(day, 'd')}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Month mode */}
                            {multiDayMode === 'month' && (
                                <div className="space-y-2">
                                    {/* Month navigation */}
                                    <div className="flex items-center justify-between">
                                        <button
                                            type="button"
                                            onClick={() => setCalendarMonth(prev => subMonths(prev, 1))}
                                            className="p-1 rounded hover:bg-[var(--color-bg-hover)] transition-colors"
                                        >
                                            <ChevronLeft className="w-4 h-4 text-[var(--color-text-secondary)]" />
                                        </button>
                                        <span className="text-sm font-semibold text-[var(--color-text)] capitalize">
                                            {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setCalendarMonth(prev => addMonths(prev, 1))}
                                            className="p-1 rounded hover:bg-[var(--color-bg-hover)] transition-colors"
                                        >
                                            <ChevronRight className="w-4 h-4 text-[var(--color-text-secondary)]" />
                                        </button>
                                    </div>

                                    {/* Day headers */}
                                    <div className="grid grid-cols-7 gap-0.5">
                                        {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map(d => (
                                            <div key={d} className="text-center text-[10px] font-semibold text-[var(--color-text-muted)] py-1">
                                                {d}
                                            </div>
                                        ))}

                                        {/* Empty cells before first day */}
                                        {Array.from({ length: monthCalendar.firstDayOfWeek }).map((_, i) => (
                                            <div key={`empty-${i}`} />
                                        ))}

                                        {/* Day cells */}
                                        {monthCalendar.daysInMonth.map((day) => {
                                            const dayStr = format(day, 'yyyy-MM-dd');
                                            const isSelected = selectedDays.includes(dayStr);
                                            const isToday = isSameDay(day, new Date());
                                            const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                                            return (
                                                <button
                                                    key={dayStr}
                                                    type="button"
                                                    onClick={() => toggleDay(dayStr)}
                                                    className={cn(
                                                        'aspect-square flex items-center justify-center rounded-md text-xs font-medium transition-all',
                                                        isSelected
                                                            ? 'bg-[var(--color-primary)] text-white'
                                                            : isToday
                                                                ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white'
                                                                : 'hover:bg-[var(--color-bg-hover)]',
                                                        isWeekend && !isSelected && 'text-[var(--color-text-muted)] opacity-50',
                                                        !isWeekend && !isSelected && !isToday && 'text-[var(--color-text)]'
                                                    )}
                                                >
                                                    {format(day, 'd')}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Quick actions */}
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={selectAllWorkdays}
                                            className="text-[10px] text-[var(--color-primary)] hover:underline"
                                        >
                                            Tous les jours ouvrés
                                        </button>
                                        <span className="text-[var(--color-text-muted)]">·</span>
                                        <button
                                            type="button"
                                            onClick={clearMonthSelection}
                                            className="text-[10px] text-[var(--color-text-muted)] hover:underline"
                                        >
                                            Tout désélectionner
                                        </button>
                                    </div>
                                </div>
                            )}

                            {selectedDays.length === 0 && (
                                <p className="text-xs text-[var(--color-error)]">
                                    Sélectionnez au moins un jour
                                </p>
                            )}
                            {selectedDays.length > 0 && (
                                <p className="text-xs text-[var(--color-text-muted)]">
                                    {selectedDays.length} jour{selectedDays.length > 1 ? 's' : ''} sélectionné{selectedDays.length > 1 ? 's' : ''}
                                </p>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Jours {isMultiDay && selectedDays.length > 1 ? '(par jour)' : ''} *
                        </label>
                        <input
                            type="number"
                            step="0.25"
                            min="0.25"
                            max="2"
                            value={formData.days}
                            onChange={(e) => setFormData({ ...formData, days: e.target.value })}
                            className="input"
                            required
                        />
                        {isMultiDay && selectedDays.length > 1 && formData.days && (
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">
                                Total : {(parseFloat(formData.days) * selectedDays.length).toFixed(2)}j pour {selectedDays.length} jours
                            </p>
                        )}
                    </div>

                    {/* Budget Progress Preview */}
                    <BudgetProgressPreview
                        project={projects.find(p => p.id === formData.projectId)}
                        additionalDays={(parseFloat(formData.days) || 0) * (isMultiDay ? selectedDays.length : 1)}
                        isEditing={!!entry}
                        editingHours={entry?.hours}
                    />

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Type de tâche
                        </label>
                        <input
                            type="text"
                            value={formData.taskType}
                            onChange={(e) => setFormData({ ...formData, taskType: e.target.value })}
                            placeholder="Ex: Développement, Réunion, Analyse..."
                            className="input"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            placeholder="Décrivez brièvement le travail effectué..."
                            className="input"
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary justify-center order-3 sm:order-1"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={(e) => handleSubmit(e, false)}
                            disabled={isSubmitting || (isMultiDay && selectedDays.length === 0)}
                            className="btn-secondary justify-center order-2 sm:order-2"
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            Enregistrer
                        </button>
                        <button
                            type="button"
                            onClick={(e) => handleSubmit(e, true)}
                            disabled={isSubmitting || (isMultiDay && selectedDays.length === 0)}
                            className="btn-primary justify-center order-1 sm:order-3"
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                            {isMultiDay && selectedDays.length > 1
                                ? `Soumettre (${selectedDays.length} jours)`
                                : 'Soumettre'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Seuil (en jours) avant la date de fin à partir duquel on affiche l'alerte orange
const PROJECT_END_WARNING_DAYS = 30;

function ProjectEndDateWarning({ project }: { project: Project | undefined }) {
    if (!project?.endDate) {
        return null;
    }

    const today = startOfDay(new Date());
    const end = startOfDay(new Date(project.endDate));
    const diffDays = differenceInCalendarDays(end, today);

    if (Number.isNaN(diffDays)) {
        return null;
    }

    // Date de fin déjà dépassée → alerte forte (rouge)
    if (diffDays < 0) {
        const elapsed = Math.abs(diffDays);
        return (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--color-error-light)] border border-[var(--color-error)]">
                <AlertTriangle className="w-4 h-4 text-[var(--color-error)] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[var(--color-error)]">
                    <span className="font-semibold">Attention : ce projet est terminé.</span>{' '}
                    Date de fin dépassée le {format(end, 'd MMMM yyyy', { locale: fr })} (il y a {elapsed} jour{elapsed > 1 ? 's' : ''}).
                    Vérifiez avant de saisir des jours.
                </div>
            </div>
        );
    }

    // Date de fin dans les 30 prochains jours → alerte de vigilance (orange)
    if (diffDays <= PROJECT_END_WARNING_DAYS) {
        return (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--color-warning-light)] border border-[var(--color-warning)]">
                <AlertCircle className="w-4 h-4 text-[var(--color-warning)] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[var(--color-warning)]">
                    <span className="font-semibold">Ce projet se termine bientôt.</span>{' '}
                    Date de fin : {format(end, 'd MMMM yyyy', { locale: fr })}{' '}
                    ({diffDays === 0 ? "aujourd'hui" : `dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`}).
                </div>
            </div>
        );
    }

    return null;
}

interface BudgetProgressPreviewProps {
    project: Project | undefined;
    additionalDays: number;
    isEditing: boolean;
    editingHours?: number;
}

function BudgetProgressPreview({ project, additionalDays, isEditing, editingHours }: BudgetProgressPreviewProps) {
    if (!project) {
        return null;
    }

    // Si pas de budget défini, afficher juste les consommations
    if (!project.budgetHours) {
        const approvedDays = (project.consumedHours || 0) / 8;
        const pendingDays = (project.pendingHours || 0) / 8;
        const adjustedPendingDays = isEditing && editingHours
            ? Math.max(0, pendingDays - (editingHours / 8))
            : pendingDays;
        const newPendingDays = adjustedPendingDays + additionalDays;
        const totalWithNewEntry = approvedDays + newPendingDays;

        return (
            <div className="p-3 bg-[var(--color-bg)] rounded-lg space-y-2">
                <div className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
                    Consommation : {project.code}
                </div>
                <div className="flex items-center justify-between text-sm">
                    <span className="text-[var(--color-text-secondary)] flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Validé
                    </span>
                    <span className="font-medium text-[var(--color-text)]">
                        {approvedDays.toFixed(2)}j
                    </span>
                </div>
                {additionalDays > 0 && (
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--color-text-muted)] flex items-center gap-1 text-xs">
                            <Clock className="w-3 h-3" />
                            Après cette saisie
                        </span>
                        <span className="font-medium text-xs text-[var(--color-text-muted)]">
                            {totalWithNewEntry.toFixed(2)}j
                            <span className="ml-1">(+{additionalDays}j)</span>
                        </span>
                    </div>
                )}
                <p className="text-xs text-[var(--color-text-muted)] italic">
                    Aucun budget défini pour ce projet
                </p>
            </div>
        );
    }

    const budgetDays = project.budgetHours / 8;
    const approvedDays = (project.consumedHours || 0) / 8;
    const pendingDays = (project.pendingHours || 0) / 8;

    // Si on édite, on soustrait les heures actuelles des pending
    const adjustedPendingDays = isEditing && editingHours
        ? Math.max(0, pendingDays - (editingHours / 8))
        : pendingDays;

    // Nouveau total en attente avec les jours saisis
    const newPendingDays = adjustedPendingDays + additionalDays;
    const totalWithNewEntry = approvedDays + newPendingDays;

    // Calcul des pourcentages
    const approvedPercent = Math.min((approvedDays / budgetDays) * 100, 100);
    const totalPercent = Math.min((totalWithNewEntry / budgetDays) * 100, 100);

    const getStatus = (percent: number) =>
        percent >= 90 ? 'danger' : percent >= 75 ? 'warning' : 'normal';

    const approvedStatus = getStatus(approvedPercent);
    const totalStatus = getStatus(totalPercent);

    const remainingDays = Math.max(0, budgetDays - approvedDays);
    const remainingAfterEntry = Math.max(0, budgetDays - totalWithNewEntry);

    return (
        <div className="p-3 bg-[var(--color-bg)] rounded-lg space-y-3">
            <div className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
                Budget projet : {project.code}
            </div>

            {/* Heures validées */}
            <div>
                <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-[var(--color-text-secondary)] flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Validé
                    </span>
                    <span
                        className={cn(
                            'font-medium text-xs',
                            approvedStatus === 'danger' && 'text-[var(--color-error)]',
                            approvedStatus === 'warning' && 'text-[var(--color-warning)]',
                            approvedStatus === 'normal' && 'text-[var(--color-text-secondary)]'
                        )}
                    >
                        {approvedDays.toFixed(2)}j / {budgetDays.toFixed(0)}j
                        <span className="text-[var(--color-text-muted)] ml-1">
                            (reste {remainingDays.toFixed(2)}j)
                        </span>
                    </span>
                </div>
                <div className="h-2 bg-[var(--color-card)] rounded-full overflow-hidden">
                    <div
                        className={cn(
                            'h-full rounded-full transition-all',
                            approvedStatus === 'danger' && 'bg-[var(--color-error)]',
                            approvedStatus === 'warning' && 'bg-[var(--color-warning)]',
                            approvedStatus === 'normal' && 'bg-[var(--color-primary)]'
                        )}
                        style={{ width: `${approvedPercent}%` }}
                    />
                </div>
            </div>

            {/* Projection avec la nouvelle saisie */}
            {additionalDays > 0 && (
                <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-[var(--color-text-muted)] flex items-center gap-1 text-xs">
                            <Clock className="w-3 h-3" />
                            Après cette saisie
                        </span>
                        <span
                            className={cn(
                                'font-medium text-xs',
                                totalStatus === 'danger' && 'text-[var(--color-error)]',
                                totalStatus === 'warning' && 'text-[var(--color-warning)]',
                                totalStatus === 'normal' && 'text-[var(--color-text-muted)]'
                            )}
                        >
                            {totalWithNewEntry.toFixed(2)}j / {budgetDays.toFixed(0)}j
                            <span className="text-[var(--color-text-muted)] ml-1">
                                (reste {remainingAfterEntry.toFixed(2)}j)
                            </span>
                        </span>
                    </div>
                    <div className="h-1.5 bg-[var(--color-card)] rounded-full overflow-hidden">
                        <div
                            className={cn(
                                'h-full rounded-full transition-all',
                                totalStatus === 'danger' && 'bg-[var(--color-error)]',
                                totalStatus === 'warning' && 'bg-[var(--color-warning)]',
                                totalStatus === 'normal' && 'bg-[var(--color-primary)] opacity-60'
                            )}
                            style={{ width: `${totalPercent}%` }}
                        />
                    </div>
                    {totalPercent >= 100 && (
                        <p className="text-xs text-[var(--color-error)] mt-1">
                            ⚠️ Cette saisie dépasse le budget du projet
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
